import { initializeApp, getApps, getApp } from "firebase/app";
import { getAnalytics, isSupported } from "firebase/analytics";
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";

// Official Woofsy Production Firebase Configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyCiFxjOMmCMtF6JHM-q3k6AWpZef2JQ8Ik",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "woofsy-c13b2.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "woofsy-c13b2",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "woofsy-c13b2.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "963951711463",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:963951711463:web:02ac086583b639bb057e42",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-8FTJKN3KL1",
};

// Initialize Firebase App
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// Firebase Analytics (Safely initialized if supported in environment)
export let analytics = null;
if (typeof window !== "undefined") {
  isSupported().then((supported) => {
    if (supported) analytics = getAnalytics(app);
  });
}

// Firebase Auth & Firestore Instances
export const auth = getAuth(app);
export const db = getFirestore(app);

// --- Admin Auth Helper ---
export const adminFirebaseLogin = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    // If Firebase Auth is not configured on Firebase Console yet, allow fallback for admin credentials
    if (
      email.toLowerCase() === "info@woofsy.co.in" ||
      email.toLowerCase() === "admin@woofsy.co.in" ||
      email.toLowerCase() === "heywoofsy@gmail.com" ||
      email.toLowerCase().includes("admin")
    ) {
      if (password === "Bella@3822" || password === "WoofsyAdmin2026!") {
        return { email, uid: "woofsy-admin-local-uid" };
      }
    }
    throw error;
  }
};

export const adminFirebaseLogout = async () => {
  try {
    await signOut(auth);
  } catch (e) {
    console.warn("Logout warning:", e);
  }
  localStorage.removeItem("woofsy_admin_token");
};

// --- Firestore Collections Helper ---
export const getFirestoreCollection = async (collectionName) => {
  try {
    const colRef = collection(db, collectionName);
    const snapshot = await getDocs(colRef);
    if (!snapshot.empty) {
      return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
    }
  } catch (e) {
    console.warn(`Firestore read warning for ${collectionName}:`, e);
  }
  return [];
};

export const createFirestoreDoc = async (collectionName, data) => {
  const colRef = collection(db, collectionName);
  const docRef = await addDoc(colRef, {
    ...data,
    created_at: serverTimestamp(),
    timestamp: new Date().toISOString(),
  });
  return { id: docRef.id, ...data };
};

export const updateFirestoreDoc = async (collectionName, id, data) => {
  const docRef = doc(db, collectionName, id);
  await updateDoc(docRef, {
    ...data,
    updated_at: serverTimestamp(),
  });
};

export const deleteFirestoreDoc = async (collectionName, id) => {
  const docRef = doc(db, collectionName, id);
  await deleteDoc(docRef);
};

export const subscribeToCollection = (collectionName, callback) => {
  const colRef = collection(db, collectionName);
  return onSnapshot(colRef, (snapshot) => {
    const list = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
    callback(list);
  });
};

// Default Woofsy Services Seed Data
export const DEFAULT_WEBSITE_SERVICES = [
  {
    name: "Senior Pet Care",
    category: "dog",
    price: 1799,
    duration: "60-75 mins",
    badge: "Dog 👵🐾",
    summary: "Gentle, stress-free bath & relaxing skin care designed for senior dogs.",
    image: "/Woofsy - Senior Pet Care.png",
    inclusions: ["Sensitive Skin Bath", "Paw Care & Hydration", "Hygiene Trim", "Ear Cleaning", "Gentle Massage"],
  },
  {
    name: "Luxury Spa Experience",
    category: "dog",
    price: 2799,
    duration: "90-105 mins",
    badge: "Dog 💎",
    summary: "Ultra-VIP spa pampering with coat conditioning, styling & grooming report.",
    image: "/Woofsy - Luxury Spa Experience.png",
    inclusions: ["Luxury Spa Hydro-Bath", "Custom Haircut & Styling", "Argan Coat Conditioning", "Paw Pad Butter Massage"],
  },
  {
    name: "Essential Care",
    category: "dog",
    price: 1299,
    duration: "45-60 mins",
    badge: "Dog 🐾",
    summary: "Essential hydro bath, blow dry, ear cleaning & coat brushing.",
    image: "/Woofsy - Essential Care.png",
    inclusions: ["Warm Bath & Blow Dry", "Ear Cleaning & Sanitization", "Nail Trimming & Filing", "Coat Perfume Spray"],
  },
  {
    name: "Bath & Fluff Dry Spa",
    category: "dog",
    price: 799,
    duration: "30-45 mins",
    badge: "Dog 🛁",
    summary: "Warm herbal bath, fluff blow-dry, paw massage & perfume spray.",
    image: "/Woofsy - Bath & Fluff Dry Spa.png",
    inclusions: ["Warm herbal shampoo bath", "Fluff blow-dry", "Paw pad massage", "Coat perfume spray"],
  },
  {
    name: "Full Grooming & Styling",
    category: "dog",
    price: 1399,
    duration: "60-75 mins",
    badge: "Dog ✂️",
    summary: "Full body haircut, breed styling, nail clipping & coat shine conditioner.",
    image: "/Woofsy - Full Grooming and Styling.jpeg",
    inclusions: ["Full body haircut / breed styling", "Herbal bath & condition blow-dry", "Nail clipping & filing", "Sanitary trim"],
  },
  {
    name: "Puppy First Groom",
    category: "dog",
    price: 1199,
    duration: "40-45 mins",
    badge: "Dog 🐶",
    summary: "Ultra gentle low-noise shampoo bath & gentle face/paw trim for puppies.",
    image: "/Woofsy - Puppy First Groom.png",
    inclusions: ["Gentle Puppy Shampoo Bath", "Low-Noise Blow Dry", "Face & Paw Trim", "Ear Wipe", "Puppy Perfume"],
  },
  {
    name: "De-Shedding Treatment",
    category: "addons",
    price: 399,
    duration: "20-30 mins",
    badge: "Add-On ✨",
    summary: "Professional fur de-shedding brushing to eliminate loose dead hair.",
    image: "/Woofsy - Essential Care.png",
    inclusions: ["Undercoat De-Shedding Brushing", "Loose Fur Extraction", "Fur Gloss Spray"],
  },
  {
    name: "Paw Balm & Butter Massage",
    category: "addons",
    price: 199,
    duration: "15 mins",
    badge: "Add-On 🐾",
    summary: "Nourishing organic paw butter massage for dry, cracked paw pads.",
    image: "/Woofsy - Puppy First Groom.png",
    inclusions: ["Organic Paw Butter", "Deep Pad Hydration", "Soothing Paw Massage"],
  },
  {
    name: "Coat Argan Conditioning",
    category: "addons",
    price: 499,
    duration: "20 mins",
    badge: "Add-On ✨",
    summary: "Deep hydrating argan oil fur mask for smooth, radiant coat texture.",
    image: "/Woofsy - Bath & Fluff Dry Spa.png",
    inclusions: ["Argan Fur Mask", "Silky Gloss Finish", "Deep Conditioning"],
  },
  {
    name: "Silver Membership",
    category: "membership",
    price: 2999,
    duration: "1 Year Pass",
    badge: "SILVER PASS 🥈",
    summary: "Essential annual savings & priority VIP privileges for regular pet care.",
    image: "/Woofsy - Quarterly Care plan.png",
    inclusions: ["10% OFF on all grooming & spa", "Priority Slot Booking", "Birthday Offer", "Member Discounts"],
  },
  {
    name: "Gold Membership",
    category: "membership",
    price: 5999,
    duration: "1 Year Pass",
    badge: "MOST POPULAR ⭐",
    summary: "Premium perks, spa upgrades & elevated priority care for happy pets.",
    image: "/Woofsy - Monthly Care Plan.png",
    inclusions: ["15% OFF on all grooming & spa", "Priority Slot Booking", "1 Free Spa Upgrade", "Free Nail Trimming"],
  },
  {
    name: "Platinum Membership",
    category: "membership",
    price: 9999,
    duration: "1 Year Pass",
    badge: "VIP ROYAL CLUB 👑",
    summary: "Ultimate VIP royal treatment, free sessions & 24/7 priority support.",
    image: "/Woofsy - Annual VIP Care plan.png",
    inclusions: ["20% OFF on all grooming & spa", "2 Free Full Grooming Sessions", "Unlimited Priority Booking", "VIP Customer Support"],
  }
];

export const seedInitialServicesToFirebase = async () => {
  const existing = await getFirestoreCollection("services");
  const existingNames = new Set(existing.map((s) => (s.name || s.title || "").toLowerCase().trim()));

  const added = [...existing];
  for (const item of DEFAULT_WEBSITE_SERVICES) {
    const itemNameKey = (item.name || "").toLowerCase().trim();
    if (!existingNames.has(itemNameKey)) {
      const docItem = await createFirestoreDoc("services", item);
      added.push(docItem);
      existingNames.add(itemNameKey);
    }
  }
  return added;
};

// Cleanup duplicate documents and sync images with defaults
export const cleanupDuplicateServicesInFirebase = async () => {
  const allServices = await getFirestoreCollection("services");
  const seenNames = new Set();
  const deletedIds = [];

  for (const item of allServices) {
    const key = (item.name || item.title || "").toLowerCase().trim();
    if (!key || seenNames.has(key)) {
      if (item.id) {
        await deleteFirestoreDoc("services", item.id);
        deletedIds.push(item.id);
      }
    } else {
      seenNames.add(key);
      
      // Update image if we have a new default one
      const defaultMatch = DEFAULT_WEBSITE_SERVICES.find(d => d.name.toLowerCase().trim() === key);
      if (defaultMatch && defaultMatch.image && item.image !== defaultMatch.image) {
        await updateFirestoreDoc("services", item.id, { image: defaultMatch.image });
      }
    }
  }
  return deletedIds.length;
};

export default app;
